import React, { useState } from "react";
import { Settings } from "lucide-react";
import Button from "../Button";
import PostingAccountsPanel from "./PostingAccountsPanel";
import EwtRecordsGrid from "./EwtRecordsGrid";
import EwtRecordsList from "./EwtRecordsList";
import Form0619ETab from "./Form0619ETab";
import Form1601EQTab from "./Form1601EQTab";
import QapDatTab from "./QapDatTab";
import CompensationTab from "./CompensationTab";
import Form1601CTab from "./Form1601CTab";
import { getWhtProfile } from "../../utils/whtProfile";

// Withholding Tax page for one accounting client (docs/WHT_IMPLEMENTATION.md
// §1). Accounting firms only — TaxObligationsPage renders it only for them,
// and the API refuses every other tenant type.
//
// Tabs are added in their own sessions (§1 order); F5 adds 0619-E, F6 1601-EQ,
// DAT-B QAP (DAT), F7a Compensation and F7b 1601-C (only for clients with employees).
const TABS = [
  { key: "ewt", label: "EWT Records" },
  { key: "0619e", label: "0619-E" },
  { key: "1601eq", label: "1601-EQ" },
  { key: "qap", label: "QAP (DAT)" },
  { key: "comp", label: "Compensation", when: (profile) => profile.hasEmployees },
  { key: "1601c", label: "1601-C", when: (profile) => profile.hasEmployees },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i);

const WithholdingTaxManagement = ({ client }) => {
  const [tab, setTab] = useState("ewt");
  const [year, setYear] = useState(currentYear);
  const [refreshKey, setRefreshKey] = useState(0);
  // Server warnings from this session, by record id, for the list's icon.
  const [sessionWarnings, setSessionWarnings] = useState({});
  const [showPostingAccounts, setShowPostingAccounts] = useState(false);
  const openPostingAccounts = () => setShowPostingAccounts(true);
  const profile = getWhtProfile(client);
  const isMicro = profile.taxpayerSize === "micro";
  const tabs = TABS.filter((t) => !t.when || t.when(profile));

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-medium text-ink">Withholding tax</h2>
          <p className="text-sm text-ink-body">{client?.name}</p>
        </div>
        <div className="flex items-end gap-3">
        <Button variant="outline" size="sm" icon={Settings} onClick={openPostingAccounts}>Posting accounts</Button>
        <label className="text-sm text-ink-body">
          <span className="mr-2">Year</span>
          <select
            aria-label="Year"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border border-ink-line rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        </div>
      </div>

      {showPostingAccounts && <PostingAccountsPanel client={client} onClose={() => setShowPostingAccounts(false)} />}

      <div className="border-b border-ink-line mb-4">
        <nav className="-mb-px flex gap-1" role="tablist" aria-label="Withholding tax sections">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`py-3 px-3 text-sm font-medium border-b-2 ${
                tab === t.key ? "border-primary-500 text-primary-700" : "border-transparent text-ink-body hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === "ewt" && (
        <div role="tabpanel" aria-label="EWT Records">
          <EwtRecordsGrid
            key={`${client?._id}-${year}`}
            client={client}
            year={year}
            defaultMonth={new Date().getMonth() + 1}
            isMicroClient={isMicro}
            onSaved={(_saved, byRecordId) => {
              setSessionWarnings((prev) => ({ ...prev, ...byRecordId }));
              setRefreshKey((k) => k + 1);
            }}
          />
          <EwtRecordsList
            client={client}
            year={year}
            refreshKey={refreshKey}
            sessionWarnings={sessionWarnings}
            onWarningsChange={(id, warnings) => setSessionWarnings((prev) => ({ ...prev, [id]: warnings }))}
            onOpenPostingAccounts={openPostingAccounts}
          />
        </div>
      )}

      {tab === "1601eq" && (
        <div role="tabpanel" aria-label="1601-EQ">
          <Form1601EQTab key={`${client?._id}-${year}`} client={client} year={year} onGoToEwtRecords={() => setTab("ewt")} onOpenPostingAccounts={openPostingAccounts} />
        </div>
      )}

      {tab === "qap" && (
        <div role="tabpanel" aria-label="QAP (DAT)">
          <QapDatTab key={`${client?._id}-${year}`} client={client} year={year} onGoToEwtRecords={() => setTab("ewt")} />
        </div>
      )}

      {tab === "comp" && profile.hasEmployees && (
        <div role="tabpanel" aria-label="Compensation">
          <CompensationTab client={client} year={year} onOpenPostingAccounts={openPostingAccounts} />
        </div>
      )}

      {tab === "1601c" && profile.hasEmployees && (
        <div role="tabpanel" aria-label="1601-C">
          <Form1601CTab key={`${client?._id}-${year}`} client={client} year={year} onGoToCompensation={() => setTab("comp")} onOpenPostingAccounts={openPostingAccounts} />
        </div>
      )}

      {tab === "0619e" && (
        <div role="tabpanel" aria-label="0619-E">
          <Form0619ETab key={`${client?._id}-${year}`} client={client} year={year} onOpenPostingAccounts={openPostingAccounts} />
        </div>
      )}
    </div>
  );
};

export default WithholdingTaxManagement;
